import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { User, Users, Building2, Building, Star, TrendingUp, Shield, Clock, Check, ArrowRight } from 'lucide-react';

const useCases = [
  {
    icon: User,
    title: "Solo Loan Officers",
    description: "Perfect for independent LOs who want to deliver a premium client experience without hiring additional staff.",
    benefits: ["No overhead costs", "Personal touch at scale", "Focus on origination"]
  },
  {
    icon: Users,
    title: "Small Teams",
    description: "Ideal for small mortgage teams looking to standardize their post-close process and maximize every closing.",
    benefits: ["Consistent client experience", "Team-wide brand presence", "Scalable solution"]
  },
  {
    icon: Building2,
    title: "Brokerages",
    description: "Enterprise solution for brokerages wanting to add a premium service offering for their loan officers.",
    benefits: ["Competitive advantage", "LO retention tool", "Volume discounts available"]
  },
  {
    icon: Building,
    title: "Lender Partners",
    description: "White-label options available for lenders who want to offer post-close concierge as a value-add service.",
    benefits: ["Brand differentiation", "Partner loyalty", "Revenue opportunity"]
  }
];

const benefits = [
  {
    icon: Star,
    title: "Reputation Building",
    description: "Systematic review collection designed to boost your online presence across all major platforms."
  },
  {
    icon: TrendingUp,
    title: "Retention & Referrals",
    description: "Keep clients in your orbit and turn satisfied borrowers into active referral sources."
  },
  {
    icon: Shield,
    title: "Brand Protection",
    description: "Every touchpoint is professionally executed and reflects your commitment to excellence."
  },
  {
    icon: Clock,
    title: "Time Freedom",
    description: "Reclaim hours every month that would otherwise go to manual follow-up tasks."
  }
];

const whatYouGet = [
  "Done-for-you post-close execution",
  "Consistent, professional client touchpoints",
  "White-label service that represents your brand",
  "Detailed monthly reporting and analytics",
  "Dedicated account support",
  "No long-term contracts or setup fees"
];

export const PartnersPage = () => {
  return (
    <div data-testid="partners-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">For Brokers & Lenders</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Partner with <span className="text-[#E5D5A5]">Better Closings</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Elevate your client experience, strengthen retention, and build a referral 
              flywheel—all without adding work to your team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why It Matters */}
      <section data-testid="why-it-matters-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Why It Matters</p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                The Post-Close <span className="text-[#E5D5A5]">Opportunity</span>
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                Most mortgage professionals know the importance of client follow-up, but few 
                have the time or systems to execute consistently. This creates a massive gap 
                in the client experience—and a missed opportunity for reviews and referrals.
              </p>
              <p className="text-neutral-400 leading-relaxed mb-8">
                Better Closings fills this gap with professional, white-glove service that 
                keeps your brand front and center while we handle all the execution.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-[#1A1A1A] border border-white/5 p-6 text-center">
                  <p className="text-[#C9A227] font-serif text-4xl font-bold mb-2">68%</p>
                  <p className="text-neutral-500 text-sm">of clients would refer but are never asked</p>
                </div>
                <div className="bg-[#1A1A1A] border border-white/5 p-6 text-center">
                  <p className="text-[#C9A227] font-serif text-4xl font-bold mb-2">92%</p>
                  <p className="text-neutral-500 text-sm">say follow-up impacts future decisions</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#C9A227]/5 blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Partnership"
                className="relative w-full h-[500px] object-cover border border-white/10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section data-testid="use-cases-section" className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Who We Serve</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              Perfect For <span className="text-[#E5D5A5]">Every Level</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`use-case-${useCase.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#C9A227]/30 transition-all duration-500 group"
              >
                <useCase.icon className="w-10 h-10 text-[#C9A227] mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-white mb-3">{useCase.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed mb-4">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center text-neutral-500 text-xs">
                      <Check className="w-3 h-3 text-[#C9A227] mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section data-testid="benefits-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">The Benefits</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              What Partnership <span className="text-[#E5D5A5]">Delivers</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-[#1A1A1A] border border-white/5 p-8 h-full group-hover:border-[#C9A227]/30 transition-all duration-500">
                  <benefit.icon className="w-12 h-12 text-[#C9A227] mx-auto mb-6" strokeWidth={1} />
                  <h3 className="font-serif text-xl text-white mb-3">{benefit.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Your Experience</p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                What You <span className="text-[#E5D5A5]">Get</span>
              </h2>
              <ul className="space-y-4">
                {whatYouGet.map((item) => (
                  <li key={item} className="flex items-center text-neutral-300">
                    <Check className="w-5 h-5 text-[#C9A227] mr-4 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-[#C9A227]/30 p-8"
            >
              <p className="text-xs text-[#C9A227] uppercase tracking-wider mb-4">Potential Impact</p>
              <p className="text-neutral-300 leading-relaxed mb-6">
                Our partners consistently report that Better Closings helps them:
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Increase review volume</span>
                  <span className="text-[#C9A227] font-semibold">Significantly</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Capture more referrals</span>
                  <span className="text-[#C9A227] font-semibold">Consistently</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Save time monthly</span>
                  <span className="text-[#C9A227] font-semibold">Hours</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-neutral-400">Client satisfaction</span>
                  <span className="text-[#C9A227] font-semibold">Enhanced</span>
                </div>
              </div>
              <p className="text-neutral-600 text-xs mt-6">
                Results vary by partner. No specific outcomes guaranteed.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to <span className="text-[#E5D5A5]">Partner?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Join the growing network of mortgage professionals who trust Better Closings 
              to elevate their post-close experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="partners-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Become a Partner
                </Button>
              </Link>
              <Link to="/pricing" data-testid="partners-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300 group">
                  View Pricing
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
